import React from 'react';
import { Check } from 'lucide-react';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import HotmartCheckoutButton from 'components/HotmartCheckoutButton/HotmartCheckoutButton';
import imgFeminismo from '../../assets/illustrations/LandingPage/site de vendas V2-25.png';
import imgCineclube from '../../assets/illustrations/LandingPage/Psicanálise Encena - Logo.png';
import imgMulheres from '../../assets/illustrations/LandingPage/site de vendas V2-27.png';

interface Course {
  id: string;
  title: string;
  description: string;
  features: string[];
  notes?: string[];
  image: string;
  available: boolean;
  checkoutUrl: string;
}

const courses: Course[] = [
  {
    id: 'feminismo-psicanalise',
    title: 'Feminismo e Psicanálise',
    description:
      'Com esse curso, você terá uma visão mais ampla e crítica do mundo a partir da psicanálise, entendendo como seus sentimentos, relações e sofrimentos são influenciados pela cultura em que vivemos.',
    features: [
      '20 aulas',
      '1 ano de acesso',
      'para todos os níveis',
    ],
    image: imgFeminismo,
    available: true,
    checkoutUrl: 'https://pay.hotmart.com/C106250453A?checkoutMode=2&off=byprgyyg',
  },
  {
    id: 'cineclube-psicanalise-encena',
    title: 'Psicanálise Encena',
    description:
      'Um curso que reúne filme e psicanálise. Uma forma leve de te fazer entender mais sobre o inconsciente e o poder dele sobre as nossas escolhas.',
    features: [
      '18 aulas',
      '6 filmes analisados',
      '1 ano de acesso',
      'com certificado',
    ],
    notes: [
      'Filmes analisados: O Conto, O Quarto de Jack, Dante Canino, Viveiro, Não Falo o Mal e Pobres Criaturas',
      '*não inclui acesso aos streamings',
    ],
    image: imgCineclube,
    available: true,
    checkoutUrl: 'https://pay.hotmart.com/L106250522L?checkoutMode=2&off=q2iayff1',
  },
  {
    id: 'mulheres-correm-lobos',
    title: 'Mulheres que Correm com os Lobos',
    description:
      'Se você já tentou ler o livro e teve dificuldade, esse Clube é para você. Aqui, além de entender o que o livro revela sobre você, você aprenderá a dar nome para o que sente e encontrar forças para seguir.',
    features: [
      '17 video aulas com análise dos capítulos',
      '17 exercícios terapêuticos',
      '17 músicas analisadas para provocar cada etapa',
      '1 ano de acesso',
      'com certificado',
    ],
    image: imgMulheres,
    available: true,
    checkoutUrl: 'https://pay.hotmart.com/K106250366M?checkoutMode=2&off=1i1yjr87',
  },
];

const CourseCard = ({ course }: { course: Course }) => (
  <div className="bg-champagne border border-[3px] border-[#1f5c57] p-2">
    <div className="h-full overflow-hidden flex flex-col items-center bg-[#e8e0d3] shadow-md p-6">
      <div className="aspect-square overflow-hidden">
        <img
          src={course.image}
          alt={course.title}
          className="w-[300px] h-[300px] object-cover"
        />
      </div>

      <div className="p-6 flex flex-col flex-1 gap-4">
        <h3 className="font-alternate font-bold text-cafe text-base uppercase leading-snug">
          {course.title}
        </h3>

        <p className="font-roboto text-cafe text-sm leading-relaxed">
          {course.description}
        </p>

        <ul className="space-y-1.5">
          {course.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm font-roboto text-cafe">
              <Check className="w-4 h-4 text-verde-musgo mt-0.5 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        {course.notes && course.notes.length > 0 && (
          <div className="space-y-1">
            {course.notes.map((note) => (
              <p key={note} className="font-roboto text-xs text-cafe/70 italic leading-relaxed">
                {note}
              </p>
            ))}
          </div>
        )}

        <div className="mt-auto pt-2">
          {course.available ? (
            <ErrorBoundary>
              <HotmartCheckoutButton checkoutUrl={course.checkoutUrl} />
            </ErrorBoundary>
          ) : (
            <button
              disabled
              className="w-full bg-verde-musgo/20 text-verde-musgo font-alternate font-bold text-sm px-6 py-3 rounded-full opacity-60 cursor-not-allowed uppercase"
            >
              Em breve
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

const CoursesSection: React.FC = () => (
  <section id="cursos" className="w-full py-16 lg:py-24 px-6 lg:px-8 bg-champagne">
    <div className="max-w-6xl mx-auto">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-libre italic text-cafe text-center mb-16">
        conteúdos disponíveis
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <CourseCard key={course.id} course={course} />
        ))}
      </div>
    </div>
  </section>
);

export default CoursesSection;
